/* ENTRADAS
 * Seção 01, Ajustes deste projeto: override por projeto, apagar devolve o
 * padrão da empresa. Seção 02, Custos e metas da empresa: vale para todos os
 * projetos. Quem grava é o `useSalvamento`; aqui só se decide O QUE gravar. */

import { Link } from 'react-router-dom';

import { CampoMoeda } from '@/componentes/campo-moeda';
import { Camada, Celula } from '@/componentes/celula';
import { Valor } from '@/componentes/valor';
import type { Cenario, ChaveCustoDireto, Integrador, Parametros, Projeto } from '@/dados';
import { formatarMoeda } from '@/design/formato';

import { Cabecalho } from './cabecalho';
import { quantia } from './grandezas';
import type { NavegarPara } from './salvamento';

/** As cinco linhas fixas de custo direto, na ordem em que a tela as mostra. */
const CUSTOS: readonly { chave: ChaveCustoDireto; rotulo: string }[] = [
  { chave: 'ca', rotulo: 'Material adicional CA' },
  { chave: 'inst', rotulo: 'Instalação' },
  { chave: 'proj', rotulo: 'Projeto elétrico' },
  { chave: 'homol', rotulo: 'Homologação' },
  { chave: 'art', rotulo: 'ART do projeto' },
];

export function Entradas({
  p,
  atual,
  integrador,
  projeto,
  revisao,
  semEmpresa,
  semProjeto,
  semVolume,
  editarProjeto,
  editarIntegrador,
  aoNavegar,
}: {
  p: Parametros;
  /** `null` quando o motor recusa opinar: não há preço deste projeto a decompor. */
  atual: Cenario | null;
  integrador: Integrador | null;
  projeto: Projeto | null;
  revisao: { projeto: number; integrador: number };
  semEmpresa: boolean;
  semProjeto: boolean;
  semVolume: boolean;
  editarProjeto: (patch: Partial<Projeto>) => void;
  editarIntegrador: (patch: Partial<Integrador>) => void;
  aoNavegar: NavegarPara;
}) {
  // Um custo direto só está sobrescrito quando o projeto guardou um NUMBER.
  // Zero digitado é override legítimo; campo vazio é ausência de override.
  function override(chave: ChaveCustoDireto): number | null {
    const bruto = projeto?.custos_diretos?.[chave];
    return typeof bruto === 'number' && Number.isFinite(bruto) ? bruto : null;
  }

  function valorCusto(chave: ChaveCustoDireto): number | null {
    const ov = override(chave);
    if (ov !== null) return ov;
    const derivado = p.custos[chave];
    return derivado > 0 ? derivado : null;
  }

  function mudarCusto(chave: ChaveCustoDireto, valor: number | null): void {
    const custos = { ...(projeto?.custos_diretos ?? {}) };
    // Campo esvaziado REMOVE a chave: o motor volta ao padrão da empresa.
    // Gravar 0 congelaria o custo derivado em zero para sempre.
    if (valor === null) delete custos[chave];
    else custos[chave] = valor;
    editarProjeto({ custos_diretos: custos });
  }

  function mudarExtra(id: string, valor: number | null): void {
    const custos = { ...(projeto?.custos_diretos ?? {}) };
    const extras = { ...(custos.extras ?? {}) };
    if (valor === null) delete extras[id];
    else extras[id] = valor;
    custos.extras = extras;
    editarProjeto({ custos_diretos: custos });
  }

  function mudarDespesa(patch: Record<string, unknown>): void {
    editarIntegrador({
      despesas: { ...(integrador?.despesas ?? {}), ...patch },
    });
  }

  return (
    <div className="favo @painel-largo:grid-cols-2">
      <Celula como="section" assenta atraso={260}>
        <Cabecalho
          titulo="Ajustes deste projeto"
          nota="Os padrões vêm da estrutura de custos da empresa. O que você digitar aqui vale só para este projeto; apagar o campo devolve o padrão."
        />
        <div className="grid gap-4 @campos:grid-cols-2">
          <CampoMoeda
            key={`preco-${revisao.projeto}`}
            rotulo="Preço de venda"
            ajuda={
              p.ticketOrigem === 'sazonalidade'
                ? 'Hoje vale a média da sazonalidade. Digite para usar o preço deste projeto.'
                : 'Valor cheio: KIT mais serviço.'
            }
            valor={p.ticket > 0 ? p.ticket : null}
            aoMudar={(v) => editarProjeto({ valor_venda: v })}
            disabled={semProjeto}
          />
          <CampoMoeda
            key={`kit-${revisao.projeto}`}
            rotulo="Custo do KIT"
            ajuda="Faturado pela distribuidora."
            valor={p.kit > 0 ? p.kit : null}
            aoMudar={(v) => editarProjeto({ custo_kit: v })}
            disabled={semProjeto}
          />
          {CUSTOS.map(({ chave, rotulo }) => (
            <CampoMoeda
              key={`${chave}-${revisao.projeto}`}
              rotulo={rotulo}
              valor={valorCusto(chave)}
              aoMudar={(v) => mudarCusto(chave, v)}
              disabled={semProjeto}
            />
          ))}
          {p.extras.map((extra) => (
            <CampoMoeda
              key={`${extra.id}-${revisao.projeto}`}
              rotulo={extra.label}
              valor={extra.valor > 0 ? extra.valor : null}
              aoMudar={(v) => mudarExtra(extra.id, v)}
              disabled={semProjeto}
            />
          ))}
        </div>
        <Camada className="mt-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-tinta-2 text-apoio">Custo direto total</span>
            <Valor valor={p.cdt} formato="moeda" tamanho="sub" />
          </div>
          <p className="apoio-nota mt-1.5">
            KIT, as cinco linhas de serviço e os extras da empresa.{' '}
            <Link
              className="text-acao font-medium underline-offset-2 hover:underline"
              to="/custos"
              onClick={aoNavegar('/custos')}
            >
              Ver estrutura de custos
            </Link>
          </p>
        </Camada>
      </Celula>

      <Celula como="section" assenta atraso={300}>
        <Cabecalho
          titulo="Custos e metas da empresa"
          nota="Estes valores valem para todos os projetos. Mudar aqui muda o preço mínimo de todo orçamento."
        />
        <div className="grid gap-4 @campos:grid-cols-2">
          <CampoMoeda
            key={`comissao-${revisao.integrador}`}
            rotulo="Comissão de vendas"
            ajuda={
              p.comissaoBase === 'servico'
                ? 'Incide sobre o serviço.'
                : 'Incide sobre a venda total.'
            }
            prefixo={null}
            sufixo="%"
            valor={p.comissao > 0 ? p.comissao : null}
            aoMudar={(v) =>
              mudarDespesa({
                comissao: {
                  ...(integrador?.despesas?.comissao ?? {}),
                  valor: v ?? 0,
                },
              })
            }
            disabled={semEmpresa}
          />
          <CampoMoeda
            key={`tributo-${revisao.integrador}`}
            rotulo="Carga tributária"
            ajuda={
              p.tributoBase === 'servico'
                ? `Incide sobre o serviço: ${formatarMoeda(atual?.baseServico ?? null)}.`
                : `Incide sobre a venda total: ${formatarMoeda(atual?.ticket ?? null)}.`
            }
            prefixo={null}
            sufixo="%"
            valor={p.tributo > 0 ? p.tributo : null}
            aoMudar={(v) =>
              mudarDespesa({
                tributo: {
                  ...(integrador?.despesas?.tributo ?? {}),
                  valor: v ?? 0,
                },
              })
            }
            disabled={semEmpresa}
          />
          <CampoMoeda
            key={`outras-${revisao.integrador}`}
            rotulo="Percentual adicional"
            ajuda="Sobre o preço de venda, para o que não está listado."
            prefixo={null}
            sufixo="%"
            valor={p.outras > 0 ? p.outras : null}
            aoMudar={(v) => mudarDespesa({ outras: v ?? 0 })}
            disabled={semEmpresa}
          />
          <CampoMoeda
            key={`fixa-${revisao.integrador}`}
            rotulo="Despesa fixa por mês"
            ajuda="Aluguel, equipe, software."
            valor={p.despFixa > 0 ? p.despFixa : null}
            aoMudar={(v) => mudarDespesa({ despFixa: v ?? 0 })}
            disabled={semEmpresa}
          />
          <CampoMoeda
            key={`meta-${revisao.integrador}`}
            rotulo="Meta de lucro líquido"
            ajuda={
              p.lucroAlvoDefinido
                ? 'É esta meta que define o preço mínimo.'
                : 'Sem meta definida, o sistema usa 12%.'
            }
            prefixo={null}
            sufixo="%"
            valor={p.lucroAlvoDefinido ? p.lucroAlvo : null}
            aoMudar={(v) => editarIntegrador({ lucro_alvo: v })}
            disabled={semEmpresa}
          />
        </div>
        <Camada className="mt-4">
          <span className="rotulo mb-1.5">Volume considerado</span>
          <p className="apoio-nota">
            {semVolume ? (
              <>
                Nenhum volume informado. O rateio da despesa fixa depende dele.{' '}
                <Link
                  className="text-acao font-medium underline-offset-2 hover:underline"
                  to="/indicadores"
                  onClick={aoNavegar('/indicadores')}
                >
                  Informar em Indicadores
                </Link>
              </>
            ) : (
              <>
                {quantia(p.V12)} projetos por ano, {quantia(p.V12 / 12)} por mês.{' '}
                {p.temSazonalidade
                  ? `Vem da sazonalidade, com ${quantia(p.coberturaSaz, 0)} meses de histórico.`
                  : 'Vem de uma média mensal antiga.'}{' '}
                <Link
                  className="text-acao font-medium underline-offset-2 hover:underline"
                  to="/sazonalidade"
                  onClick={aoNavegar('/sazonalidade')}
                >
                  {p.temSazonalidade ? 'Editar sazonalidade' : 'Informar o histórico'}
                </Link>
              </>
            )}
          </p>
        </Camada>
      </Celula>
    </div>
  );
}
